import { client as braintreeClient, threeDSecure } from 'braintree-web';

import logger from '@eventbrite/client-logger';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';

import {
    getBraintreeClientToken as getBraintreeClientTokenApi,
    getBraintreeNonce,
} from '../api/braintreeClient';
import {
    BRAINTREE_STATUS_LOOKUP_ENROLLED,
    BRAINTREE_TRANSSTATUS_AUTHENTICATION_NOT_PERFORMED,
    MAX_AMOUNT_EXEMPTION_REQUEST,
    PAYMENT_METHODS,
} from '../constants';
import { gettext } from '@eventbrite/i18n';

const is3DSecureRequiredEvent = (currency) => ['EUR', 'GBP'].includes(currency);

const isCreditCardOrVaultPaymentMethod = (paymentData) =>
    [PAYMENT_METHODS.CREDIT, PAYMENT_METHODS.USER_INSTRUMENT].includes(
        paymentData.paymentMethod,
    );

const is3DSecure2Error = (status, transStatus) =>
    status === BRAINTREE_STATUS_LOOKUP_ENROLLED &&
    transStatus !== BRAINTREE_TRANSSTATUS_AUTHENTICATION_NOT_PERFORMED;

const getBraintreeNoncePromise = ({
    paymentData,
    currency,
    countryCode,
    amount,
    orderId,
    eventId,
    userCountry,
}) =>
    getBraintreeNonce({
        paymentData,
        currency,
        countryCode,
        amount,
        orderId,
        eventId,
        userCountry,
    })
        .then(({ response: braintreeNonceResponse }) => braintreeNonceResponse)
        .catch(() => ({ needs_three_domain_secure: false }));

export const shouldPerform3DS2Validation = ({ state, paymentData }) => {
    const {
        app: {
            featureFlags: { enable3DSecure2: enable3DSecure2FeatureFlag },
        },
        event: { currency },
    } = state;

    const isCreditCardOrVault = isCreditCardOrVaultPaymentMethod(paymentData);

    return (
        enable3DSecure2FeatureFlag &&
        isCreditCardOrVault &&
        is3DSecureRequiredEvent(currency)
    );
};

export const getThreeDomainSecureNonce = async ({
    paymentData,
    currency,
    countryCode,
    amount,
    orderId,
    eventId,
    userCountry,
}) => {
    try {
        const braintreeNonceResponse = await getBraintreeNoncePromise({
            paymentData,
            currency,
            countryCode,
            amount,
            orderId,
            eventId,
            userCountry,
        });

        if (!braintreeNonceResponse.needs_three_domain_secure) {
            return null;
        }

        const braintreeClientTokenResponse = await getBraintreeClientTokenApi(
            currency,
        );

        const braintreeClientToken =
            braintreeClientTokenResponse.response.client_token;

        const clientInstance = await braintreeClient.create({
            authorization: braintreeClientToken,
        });

        const threeDSecureInstance = await threeDSecure.create({
            client: clientInstance,
            version: 2,
        });

        const verifiedNonce = await new Promise((resolve, reject) => {
            threeDSecureInstance.verifyCard(
                {
                    amount,
                    nonce: braintreeNonceResponse.nonce,
                    exemptionRequested: amount < MAX_AMOUNT_EXEMPTION_REQUEST,
                    onLookupComplete: (data, next) => {
                        const reactRoot =
                            document.querySelectorAll('.eds-modal')[0];

                        reactRoot.style.zIndex = -1;
                        reactRoot.children[0].style.zIndex = -1;
                        next();
                    },
                },
                (err, payload) => {
                    // Restore original z-index values
                    const reactRoot =
                        document.querySelectorAll('.eds-modal')[0];

                    if (reactRoot) {
                        reactRoot.style.zIndex = '';
                        reactRoot.children[0].style.zIndex = '';
                    }

                    const genericErrorMessage = gettext(
                        'Validation failed. Try again or use another payment method',
                    );

                    if (err) {
                        logger.error(`Braintree 3DSecure2 error: ${err}`);
                        reject(genericErrorMessage);
                    }

                    const {
                        threeDSecureInfo: {
                            status,
                            lookup: { transStatus },
                        },
                    } = payload;

                    // We use the status of the 3DSecure2 authentication to go through with the payment
                    // or throw an error.
                    if (is3DSecure2Error(status, transStatus)) {
                        reject(genericErrorMessage);
                    } else {
                        resolve(payload.nonce);
                    }
                },
            );
        });

        return verifiedNonce;
    } catch (error) {
        throw new CustomSubmissionError(
            {
                _error: error,
            },
            {},
        );
    }
};
